import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment as env } from '../../environments/environment';

export interface UserAlertChannelPrefs {
  enabled: boolean;
  email: boolean;
  inApp: boolean;
}

/** Mirror of the backend `UserAlertOverrides` shape — same nested structure
 *  as `Restaurant.alertSettings` so a single mental model fits both tabs. */
export interface UserAlertOverrides {
  stock?: Partial<
    Record<
      | 'lowStock'
      | 'outOfStock'
      | 'restockSuggestion'
      | 'productExpiringSoon'
      | 'productExpired'
      | 'internalOrderUpdate',
      UserAlertChannelPrefs
    >
  >;
  staff?: Partial<
    Record<
      | 'planningPublished'
      | 'planningModified'
      | 'leaveRequestAnswered'
      | 'leaveRequestCreated'
      | 'leaveRequestCancelled'
      | 'salaryAdvanceRequestCreated'
      | 'salaryAdvanceRequestAnswered'
      | 'shiftChanged'
      | 'taskAssigned',
      UserAlertChannelPrefs
    >
  >;
  maintenance?: Partial<
    Record<
      'equipmentMaintenance' | 'equipmentMalfunction' | 'cleaningSupplyLow',
      UserAlertChannelPrefs
    >
  >;
  reservation?: Partial<
    Record<
      'newReservation' | 'modifiedReservation' | 'cancelledReservation' | 'arrivalReminder',
      UserAlertChannelPrefs
    >
  >;
  purchasing?: Partial<
    Record<'supplierInvoiceDueSoon' | 'supplierInvoiceOverdue', UserAlertChannelPrefs>
  >;
}

export interface OrderPreparationPrefs {
  awayFromPage: boolean;
  /** Empty means every station. */
  functionalCategoryIds: string[];
}

export interface UserAlertPreferences {
  overrides: UserAlertOverrides;
  orderPreparation?: OrderPreparationPrefs;
}

export type UpdateUserAlertPreferencesPayload = {
  overrides?: UserAlertOverrides;
  orderPreparation?: Partial<OrderPreparationPrefs>;
};

export interface OrderPreparationRecipient extends OrderPreparationPrefs {
  userId: string;
  firstName: string;
  lastName: string;
  roleName?: string;
}

@Injectable({ providedIn: 'root' })
export class UserAlertPreferencesService {
  private readonly http = inject(HttpClient);

  getMine(restaurantId: string): Observable<UserAlertPreferences> {
    return this.http.get<UserAlertPreferences>(
      `${env.apiUrl}/alert-preferences/me/${restaurantId}`
    );
  }

  updateMine(
    restaurantId: string,
    payload: UpdateUserAlertPreferencesPayload
  ): Observable<UserAlertPreferences> {
    return this.http.patch<UserAlertPreferences>(
      `${env.apiUrl}/alert-preferences/me/${restaurantId}`,
      payload
    );
  }

  listOrderPreparationRecipients(restaurantId: string): Observable<OrderPreparationRecipient[]> {
    return this.http.get<OrderPreparationRecipient[]>(
      `${env.apiUrl}/alert-preferences/order-preparation/${restaurantId}`
    );
  }

  updateOrderPreparationFor(
    restaurantId: string,
    userId: string,
    payload: Partial<OrderPreparationPrefs>
  ): Observable<OrderPreparationRecipient> {
    return this.http.patch<OrderPreparationRecipient>(
      `${env.apiUrl}/alert-preferences/order-preparation/${restaurantId}/${userId}`,
      payload
    );
  }
}
